/*
  Google Merchant Center "automated discounts"
  https://support.google.com/merchants/answer/16961497

  Shopping ads link to the product page with a signed price token in the `pv2`
  URL parameter (ES256 JWT). This script:
  - verifies the token and shows the discounted price on the product page
    (Google requires it for at least 30 minutes, also after the token is gone
    from the URL), with the EEA prior price when the token carries one,
  - sends the token to the Magecraft app (app proxy), which verifies it again
    and returns a single-use discount code valid for 48 hours,
  - keeps that code applied to the cart, so the price holds in checkout.

  Config comes from `window.googleAutomatedDiscounts` (layout/theme.liquid).
*/
(() => {
  if (window.GoogleAutomatedDiscounts) return;

  const GOOGLE_PUBLIC_KEY =
    'MFkwEwYHKoZIzj0CAQYIKoZIzj0DAQcDQgAERUlUpxshr67EO66ZTX0Fpog0LEHcnUnlSsIrOfroxTLu2XnigBK/lfYRxzQWq9K6nqsSjjYeea0T12r+y3nvqg==';
  const STORAGE_KEY = 'magecraft:google-automated-discounts';
  const DISPLAY_TTL = 30 * 60 * 1000;
  const CODE_TTL = 48 * 60 * 60 * 1000;

  const config = window.googleAutomatedDiscounts || {};
  const merchantIds = String(config.merchantIds || '')
    .split(',')
    .map((id) => id.trim())
    .filter(Boolean);

  const base64UrlDecode = (value) => {
    const base64 = value.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    return Uint8Array.from(atob(padded), (char) => char.charCodeAt(0));
  };

  const decodeJson = (value) => JSON.parse(new TextDecoder().decode(base64UrlDecode(value)));

  const toCents = (amount) => {
    const number = Number(amount);
    return Number.isFinite(number) ? Math.round(number * 100) : null;
  };

  let publicKey;
  const getPublicKey = () => {
    publicKey ||= crypto.subtle.importKey(
      'spki',
      base64UrlDecode(GOOGLE_PUBLIC_KEY),
      { name: 'ECDSA', namedCurve: 'P-256' },
      false,
      ['verify']
    );
    return publicKey;
  };

  // Logs why a token was rejected; there's no other trace of it on the page.
  const reject = (reason, detail) => {
    console.warn(`[Google automated discounts] Token rejected: ${reason}`, detail ?? '');
    return null;
  };

  // Returns the token payload, or null when the token must be rejected.
  const verifyToken = async (token, key) => {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return reject('malformed');
      const [encodedHeader, encodedPayload, encodedSignature] = parts;

      const header = decodeJson(encodedHeader);
      const headerKeys = Object.keys(header);
      if (headerKeys.length !== 2 || header.alg !== 'ES256' || header.typ !== 'JWT') return reject('header', header);

      const valid = await crypto.subtle.verify(
        { name: 'ECDSA', hash: 'SHA-256' },
        await (key || getPublicKey()),
        base64UrlDecode(encodedSignature),
        new TextEncoder().encode(`${encodedHeader}.${encodedPayload}`)
      );
      if (!valid) return reject('signature');

      const payload = decodeJson(encodedPayload);
      if (!(Number(payload.exp) * 1000 > Date.now())) return reject('expired', new Date(payload.exp * 1000));
      if (merchantIds.length && !merchantIds.includes(String(payload.m))) return reject('merchant ID', payload.m);
      if (toCents(payload.p) === null) return reject('price', payload.p);
      return payload;
    } catch (error) {
      return reject('error', error);
    }
  };

  const readStore = () => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
    } catch (error) {
      return {};
    }
  };

  const expiresAt = (entry) => Math.max(entry.displayUntil || 0, entry.codeUntil || 0, entry.tokenUntil || 0);

  const writeStore = (store) => {
    const now = Date.now();
    Object.keys(store).forEach((variantId) => {
      if (expiresAt(store[variantId]) < now) delete store[variantId];
    });
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
    } catch (error) {}
  };

  // The discount for a variant, while it should still be shown.
  const getEntry = (variantId) => {
    const entry = readStore()[variantId];
    if (!entry || entry.currency !== config.currency) return null;
    const now = Date.now();
    return now < entry.displayUntil || (entry.code && now < entry.codeUntil) ? entry : null;
  };

  const updateEntry = (variantId, changes) => {
    const store = readStore();
    if (changes === null) delete store[variantId];
    else store[variantId] = { ...store[variantId], ...changes };
    writeStore(store);
  };

  const cartUrl = (action) => `${window.routes?.cart_url || '/cart'}${action}.js`;

  // Adds the stored codes the cart is missing, keeping the codes already in it.
  const applyCodes = async () => {
    const now = Date.now();
    const codes = Object.values(readStore())
      .filter((entry) => entry.code && now < entry.codeUntil && entry.currency === config.currency)
      .map((entry) => entry.code);
    if (!codes.length) return;

    try {
      const cart = await (await fetch(cartUrl(''))).json();
      const cartCodes = (cart.discount_codes || []).map(({ code }) => code);
      const missing = codes.filter((code) => !cartCodes.some((cartCode) => cartCode.toUpperCase() === code.toUpperCase()));
      if (!missing.length) return;

      await fetch(cartUrl('/update'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: [...cartCodes, ...missing].join(',') }),
      });
    } catch (error) {}
  };

  const pendingRequests = {};

  // Exchanges the token for a discount code. Resolves false when the app rejects
  // the token (400/422); other errors (setup problems like 401/403/404, server
  // and network errors) are retried on the next page view.
  const requestCode = (variantId) => {
    pendingRequests[variantId] ||= (async () => {
      const entry = readStore()[variantId];
      if (!entry || entry.code || !config.proxyUrl) return true;

      try {
        const response = await fetch(config.proxyUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ token: entry.token }),
        });
        if (response.status === 400 || response.status === 422) {
          updateEntry(variantId, null);
          return false;
        }
        if (!response.ok) return true;

        const { code, expires_at: expires } = await response.json();
        if (!code) return true;
        updateEntry(variantId, { code, codeUntil: Date.parse(expires) || Date.now() + CODE_TTL });
        await applyCodes();
      } catch (error) {}
      return true;
    })().finally(() => delete pendingRequests[variantId]);
    return pendingRequests[variantId];
  };

  // Stores a verified token from the URL; `variantPrices` maps variant ids of the
  // current product to their prices in cents.
  const acceptToken = async (token, variantPrices) => {
    const payload = await verifyToken(token);
    if (!payload) return null;

    const variantId = String(payload.o);
    const price = toCents(payload.p);
    if (!(variantId in variantPrices)) return reject('offer is not a variant of this product', variantId);
    if (payload.c !== config.currency) return reject('currency', `${payload.c} (store: ${config.currency})`);
    if (price >= variantPrices[variantId]) return reject('price not lower', `${price} >= ${variantPrices[variantId]}`);

    const existing = readStore()[variantId];
    const sameOffer = existing && existing.price === price && existing.currency === payload.c;
    updateEntry(variantId, {
      token,
      tokenUntil: payload.exp * 1000,
      price,
      prior: toCents(payload.pp),
      currency: payload.c,
      displayUntil: Date.now() + DISPLAY_TTL,
      code: sameOffer ? existing.code : null,
      codeUntil: sameOffer ? existing.codeUntil : null,
    });
    return variantId;
  };

  const formatMoney = (cents, format = config.moneyFormat || '{{amount}}') => {
    const formatNumber = (decimals, thousands = ',', decimal = '.') => {
      const [integer, fraction] = (cents / 100).toFixed(decimals).split('.');
      return integer.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (fraction ? decimal + fraction : '');
    };

    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (match, placeholder) => {
      switch (placeholder) {
        case 'amount_no_decimals':
          return formatNumber(0);
        case 'amount_with_comma_separator':
          return formatNumber(2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return formatNumber(0, '.', ',');
        case 'amount_with_apostrophe_separator':
          return formatNumber(2, "'", '.');
        case 'amount_with_space_separator':
          return formatNumber(2, ' ', ',');
        case 'amount_no_decimals_with_space_separator':
          return formatNumber(0, ' ', ',');
        case 'amount_with_period_and_space_separator':
          return formatNumber(2, ' ', '.');
        default:
          return formatNumber(2);
      }
    });
  };

  // Drops the offer behind a code the customer removed in the cart, so
  // applyCodes does not put it back (and its price is no longer shown).
  const forgetCode = (code) => {
    const store = readStore();
    Object.keys(store).forEach((variantId) => {
      if (store[variantId].code?.toUpperCase() === String(code).toUpperCase()) delete store[variantId];
    });
    writeStore(store);
  };

  window.GoogleAutomatedDiscounts = { verifyToken, getEntry, applyCodes, formatMoney, forgetCode };

  /*
    Product page: rendered by snippets/google-discount-price.liquid next to the
    price, swaps the price markup in `#price-{section id}` (and the sticky
    add-to-cart bar) for the discounted one.
  */
  class GoogleDiscountPrice extends HTMLElement {
    connectedCallback() {
      this.variantPrices = JSON.parse(this.dataset.variantPrices || '{}');
      // Deferred, so it runs after the other variant change subscribers re-render prices.
      this.variantChangeUnsubscriber = window.subscribe?.(PUB_SUB_EVENTS.variantChange, ({ data }) => {
        if (data.sectionId !== this.dataset.sectionId || !data.variant) return;
        setTimeout(() => this.render(String(data.variant.id)));
      });
      this.init();
    }

    disconnectedCallback() {
      this.variantChangeUnsubscriber?.();
    }

    async init() {
      const token = new URLSearchParams(window.location.search).get('pv2');
      if (token) await acceptToken(token, this.variantPrices);
      this.render(this.currentVariantId());

      Object.keys(this.variantPrices).forEach(async (variantId) => {
        const entry = readStore()[variantId];
        if (!entry || entry.code || !(Date.now() < entry.tokenUntil)) return;
        if (!(await requestCode(variantId))) this.render(this.currentVariantId());
      });
    }

    currentVariantId() {
      const input = document.querySelector(`#product-form-${this.dataset.sectionId} [name="id"]`);
      return String(input?.value || Object.keys(this.variantPrices)[0]);
    }

    // Updates the main price and the sticky add-to-cart bar's price.
    render(variantId) {
      const { sectionId } = this.dataset;
      this.originalHtml ||= {};
      this.renderPrice(`price-${sectionId}`, variantId, { full: true });
      this.renderPrice(`StickyPrice-${sectionId}`, variantId, { full: false });
    }

    renderPrice(containerId, variantId, { full }) {
      const container = document.getElementById(containerId);
      if (!container) return;

      // On variant change the theme replaces the container's content, so the
      // marker sits on the inner `.price` and goes away with it.
      if (container.querySelector('[data-google-discount]')) container.innerHTML = this.originalHtml[containerId];

      const entry = getEntry(variantId);
      const price = container.querySelector('.price');
      const sale = price?.querySelector('.price__sale');
      if (!entry || !sale) return;

      // In the EEA the struck-through price is the prior price Google passes.
      const struck = entry.prior || this.variantPrices[variantId];
      const percent = struck > entry.price ? Math.round(((struck - entry.price) / struck) * 100) : 0;
      const { regularLabel, saleLabel } = this.dataset;
      const unit = full && this.dataset.unit;
      const priorTemplate = full && this.dataset.priorTemplate;

      this.originalHtml[containerId] = container.innerHTML;
      price.dataset.googleDiscount = variantId;
      price.classList.add('price--on-sale');
      price.classList.remove('price--no-compare');
      sale.innerHTML = `
        ${
          percent
            ? `<div class="price__sale-saving-wrapper">
                <span class="visually-hidden visually-hidden--inline">${regularLabel}</span>
                <span><s class="price-item price-item--regular">${formatMoney(struck)}</s></span>
                <div class="price__sale-percentage-saving">-${percent}%</div>
              </div>`
            : ''
        }
        <span class="visually-hidden visually-hidden--inline">${saleLabel}</span>
        <span class="price-item price-item--sale price-item--last">
          ${formatMoney(entry.price)}
          ${unit ? `<span class="price__unit">${unit}</span>` : ''}
        </span>
        ${
          entry.prior && priorTemplate
            ? `<small class="price__prior caption">${priorTemplate.replace('[amount]', formatMoney(entry.prior))}</small>`
            : ''
        }
      `;
    }
  }

  if (!customElements.get('google-discount-price')) {
    customElements.define('google-discount-price', GoogleDiscountPrice);
  }

  // Every page: keep the codes on the cart, and finish code requests that failed.
  const keepCodesApplied = () => {
    const now = Date.now();
    Object.entries(readStore()).forEach(([variantId, entry]) => {
      if (!entry.code && now < entry.tokenUntil && entry.currency === config.currency) requestCode(variantId);
    });
    applyCodes();
    window.subscribe?.(PUB_SUB_EVENTS.cartUpdate, applyCodes);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', keepCodesApplied);
  else keepCodesApplied();
})();
